import React, { useState, useEffect } from 'react';
import { useRouter, Link } from '../../router/Router';
import { useAuth } from '../../auth/AuthContext';
import { paymentApi } from '../../services/paymentApi';
import {
  CreditCard,
  Ticket,
  User,
  GraduationCap,
  Bus,
  Bell,
  LogOut,
  Clock,
  X,
  ExternalLink,
} from 'lucide-react';
import { AdminDashboard } from './AdminDashboard';
import { AdminPaymentVerification } from './AdminPaymentVerification';
import { AdminPaymentSettings } from './AdminPaymentSettings';
import { AdminBookings } from './AdminBookings';
import { AdminStudents } from './AdminStudents';
import { AdminStudentAccountCreator } from './AdminStudentAccountCreator';
import { AdminUniversities } from './AdminUniversities';
import { AdminTrips } from './AdminTrips';
import { AdminVehicles } from './AdminVehicles';
import { AdminNotifications } from './AdminNotifications';
import { AdminSettings } from './AdminSettings';
import { AdminDemandRequests } from './AdminDemandRequests';
import { AdminStudentDemand } from './demand/AdminStudentDemand';
import { AdminAuditLogs } from './AdminAuditLogs';
import { AdminTreasury } from './AdminTreasury';
import { AdminPromoAndRewards } from './AdminPromoAndRewards';
import { AdminStaffManagement } from './AdminStaffManagement';
import { demandRequestService } from '../../services/demandRequestService';
import { Send, ShieldCheck, BarChart2, UserPlus, Users, ShieldAlert } from 'lucide-react';

const OperatorAccessDeniedCard: React.FC = () => {
  const { navigate } = useRouter();
  return (
    <div
      style={{
        padding: '3rem 1.5rem',
        textAlign: 'center',
        maxWidth: '540px',
        margin: '2rem auto',
      }}
    >
      <div
        style={{
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '20px',
          padding: '2.5rem 2rem',
          boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.1)',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            background: '#FEE2E2',
            color: '#DC2626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.25rem auto',
          }}
        >
          <ShieldAlert size={32} />
        </div>
        <h3 style={{ fontSize: '1.35rem', fontWeight: '800', color: '#991B1B', marginBottom: '0.75rem' }}>
          منطقة مخصصة للإدارة العليا فقط
        </h3>
        <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '1.75rem' }}>
          عذرًا، حساب التشغيل الخاص بك لا يمتلك صلاحية الوصول إلى هذا القسم (البيانات المالية وإعدادات النظام الحساسة). يمكنك متابعة وإدارة الأقسام التشغيلية المخصصة لك.
        </p>
        <button
          onClick={() => navigate('/admin')}
          style={{
            background: '#E5232A',
            color: '#fff',
            border: 'none',
            padding: '10px 24px',
            borderRadius: '12px',
            fontWeight: '700',
            cursor: 'pointer',
          }}
        >
          العودة للوحة القيادة التشغيلية
        </button>
      </div>
    </div>
  );
};

export const AdminLayout: React.FC = () => {
  const { currentPath, navigate } = useRouter();
  const { user, logout, isOperator, staffProfile, hasSectionAccess } = useAuth();
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [pendingDemandCount, setPendingDemandCount] = useState<number>(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  // Poll or fetch pending count
  useEffect(() => {
    const fetchPending = async () => {
      try {
        const [res, demandList] = await Promise.all([
          paymentApi.getPendingManualPayments(),
          demandRequestService.getAllDemandRequests(),
        ]);
        if (res.success && res.data) {
          setPendingCount(res.data.length);
        }
        const pendingDemands = demandList.filter((d) => d.status === 'PENDING');
        setPendingDemandCount(pendingDemands.length);
      } catch (err) {
        console.warn('Could not fetch pending counts for badges:', err);
      }
    };

    fetchPending();
    const interval = setInterval(fetchPending, 30000); // 30s refresh
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="admin-app-layout" dir="rtl">
      {/* Top Header */}
      <header className="admin-topbar">
        <div className="admin-topbar-inner">
          <div className="admin-topbar-start">
            <button
              className="admin-menu-toggle-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              title="القائمة"
            >
              {mobileMenuOpen ? (
                <X size={20} />
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              )}
            </button>

            <Link to="/admin" className="admin-brand-link">
              <span className="admin-logo-pill" style={{ padding: '0', overflow: 'hidden', width: '34px', height: '34px', borderRadius: '10px' }}>
                <img src="/oto-logo.jpg" alt="OTO" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </span>
              <span className="admin-brand-text">
                <span className="admin-brand-desktop-text">لوحة إدارة OTO STUDENTS</span>
                <span className="admin-brand-mobile-text">لوحة الإدارة</span>
              </span>
            </Link>
          </div>

          <div className="admin-topbar-end">
            <Link to="/student" className="btn-switch-to-student desktop-only-flex" title="بوابة الطالب">
              <ExternalLink size={15} />
              <span>بوابة الطالب</span>
            </Link>

            <div className="admin-user-pill desktop-only-flex">
              <span className="admin-user-name">
                {staffProfile?.fullName || user?.email || 'admin@otostudents.com'}
              </span>
              <span
                className="admin-role-tag"
                style={{
                  background: isOperator ? '#E0F2FE' : undefined,
                  color: isOperator ? '#0369A1' : undefined,
                  borderColor: isOperator ? '#BAE6FD' : undefined,
                }}
              >
                {isOperator ? 'موظف تشغيل' : 'مدير النظام'}
              </span>
            </div>

            <button onClick={handleLogout} className="admin-logout-btn" title="تسجيل الخروج">
              <LogOut size={16} />
              <span className="desktop-only-inline">خروج</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer Backdrop */}
      {mobileMenuOpen && (
        <div
          className="admin-sidebar-backdrop"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main Container: Sidebar + Content */}
      <div className="admin-main-container">
        {/* Sidebar */}
        <aside className={`admin-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
          {/* Mobile Admin Profile Card inside Drawer */}
          <div className="admin-sidebar-mobile-card">
            <div className="admin-sidebar-mobile-user-row">
              <div
                className="admin-sidebar-avatar"
                style={{
                  background: isOperator ? '#E0F2FE' : undefined,
                  color: isOperator ? '#0369A1' : undefined,
                }}
              >
                {(staffProfile?.fullName || user?.email || 'A').charAt(0).toUpperCase()}
              </div>
              <div className="admin-sidebar-user-details">
                <span className="admin-sidebar-email">
                  {staffProfile?.fullName || user?.email || 'admin@otostudents.com'}
                </span>
                <span
                  className="admin-sidebar-badge"
                  style={{
                    background: isOperator ? '#E0F2FE' : undefined,
                    color: isOperator ? '#0369A1' : undefined,
                  }}
                >
                  {isOperator ? 'موظف تشغيل' : 'مدير النظام'}
                </span>
              </div>
            </div>
            <div className="admin-sidebar-quick-actions">
              <Link
                to="/student"
                className="admin-sidebar-action-btn switch-btn"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ExternalLink size={14} />
                <span>بوابة الطالب</span>
              </Link>
              <button
                onClick={handleLogout}
                className="admin-sidebar-action-btn logout-btn"
              >
                <LogOut size={14} />
                <span>تسجيل الخروج</span>
              </button>
            </div>
          </div>

          <nav className="admin-sidebar-nav">
            {/* GROUP 1: العمليات والحجوزات (Operations & Bookings) */}
            <div className="sidebar-group-title">العمليات والحجوزات اليومية</div>

            {/* Dashboard */}
            {hasSectionAccess('DASHBOARD') && (
              <Link
                to="/admin"
                className={`sidebar-nav-item ${currentPath === '/admin' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="7" height="9" />
                  <rect x="14" y="3" width="7" height="5" />
                  <rect x="14" y="12" width="7" height="9" />
                  <rect x="3" y="16" width="7" height="5" />
                </svg>
                <span>لوحة القيادة (Dashboard)</span>
              </Link>
            )}

            {/* Bookings & Calendar */}
            {hasSectionAccess('BOOKINGS') && (
              <Link
                to="/admin/bookings"
                className={`sidebar-nav-item ${currentPath === '/admin/bookings' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Ticket size={18} />
                <span className="flex-1">إدارة الحجوزات والعمليات</span>
                <span className="text-[10px] bg-rose-100 text-[#E5232A] font-bold px-1.5 py-0.5 rounded">الكلندر</span>
              </Link>
            )}

            {/* Trips */}
            {hasSectionAccess('TRIPS') && (
              <Link
                to="/admin/trips"
                className={`sidebar-nav-item ${currentPath === '/admin/trips' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Bus size={18} />
                <span>جدول الرحلات والمواعيد</span>
              </Link>
            )}

            {/* Demand Requests & Analytics */}
            {hasSectionAccess('DEMAND') && (
              <>
                <Link
                  to="/admin/demand-requests"
                  className={`sidebar-nav-item ${
                    currentPath === '/admin/demand-requests' ? 'active' : ''
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Send size={18} />
                  <span className="flex-1">طلبات الرحلات المباشرة</span>
                  {pendingDemandCount > 0 ? (
                    <span className="sidebar-pending-badge">{pendingDemandCount} جديد</span>
                  ) : (
                    <span className="sidebar-zero-badge">0</span>
                  )}
                </Link>

                <Link
                  to="/admin/student-demand"
                  className={`sidebar-nav-item ${
                    currentPath === '/admin/student-demand' ? 'active' : ''
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <BarChart2 size={18} />
                  <span className="flex-1">بيانات وتحليلات الطلب (Demand)</span>
                </Link>
              </>
            )}

            {/* GROUP 2: شؤون الطلاب والحسابات (Students & Accounts) */}
            <div className="sidebar-group-title mt-4">شؤون الطلاب والحسابات</div>

            {/* Students */}
            {hasSectionAccess('STUDENTS') && (
              <Link
                to="/admin/students"
                className={`sidebar-nav-item ${currentPath === '/admin/students' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <User size={18} />
                <span>الطلاب المسجلين</span>
              </Link>
            )}

            {/* Create Student Account & WhatsApp Handover */}
            {hasSectionAccess('CREATE_STUDENT') && (
              <Link
                to="/admin/create-student"
                className={`sidebar-nav-item ${currentPath === '/admin/create-student' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <UserPlus size={18} />
                <span className="flex-1">إنشاء حسابات الطلاب (واتساب)</span>
                <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded">جديد</span>
              </Link>
            )}

            {/* Promo Codes & Student Rewards */}
            {hasSectionAccess('PROMO_REWARDS') && (
              <Link
                to="/admin/promo-rewards"
                className={`sidebar-nav-item ${currentPath === '/admin/promo-rewards' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 12v10H4V12" />
                  <path d="M2 7h20v5H2z" />
                  <path d="M12 22V7" />
                  <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                  <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                </svg>
                <span className="flex-1">البروموكود ومكافآت الطلاب</span>
                <span className="text-[10px] bg-rose-100 text-[#E5232A] font-bold px-1.5 py-0.5 rounded">مكافآت</span>
              </Link>
            )}

            {/* GROUP 3: المالية والخزينة (Finance & Treasury) */}
            {(hasSectionAccess('PAYMENTS') || hasSectionAccess('TREASURY') || hasSectionAccess('PAYMENT_SETTINGS')) && (
              <>
                <div className="sidebar-group-title mt-4">المالية والخزينة</div>

                {/* Payment Verification */}
                {hasSectionAccess('PAYMENTS') && (
                  <Link
                    to="/admin/payments"
                    className={`sidebar-nav-item ${currentPath === '/admin/payments' ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Clock size={18} />
                    <span className="flex-1">اعتماد المدفوعات</span>
                    {pendingCount > 0 ? (
                      <span className="sidebar-pending-badge">{pendingCount} معلق</span>
                    ) : (
                      <span className="sidebar-zero-badge">0</span>
                    )}
                  </Link>
                )}

                {/* Treasury & Financial Transactions */}
                {hasSectionAccess('TREASURY') && (
                  <Link
                    to="/admin/treasury"
                    className={`sidebar-nav-item ${currentPath === '/admin/treasury' ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <CreditCard size={18} />
                    <span className="flex-1">الخزينة والتعاملات المالية</span>
                    <span className="text-[10px] bg-amber-100 text-amber-800 font-bold px-1.5 py-0.5 rounded">مالية</span>
                  </Link>
                )}

                {/* Payment Settings */}
                {hasSectionAccess('PAYMENT_SETTINGS') && (
                  <Link
                    to="/admin/payment-settings"
                    className={`sidebar-nav-item ${
                      currentPath === '/admin/payment-settings' ? 'active' : ''
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <CreditCard size={18} />
                    <span>إعدادات وسائل الدفع</span>
                  </Link>
                )}
              </>
            )}

            {/* GROUP 4: الأسطول والجامعات (Fleet & Universities) */}
            {(hasSectionAccess('UNIVERSITIES') || hasSectionAccess('VEHICLES')) && (
              <>
                <div className="sidebar-group-title mt-4">الأسطول والجامعات</div>

                {/* Universities */}
                {hasSectionAccess('UNIVERSITIES') && (
                  <Link
                    to="/admin/universities"
                    className={`sidebar-nav-item ${currentPath === '/admin/universities' ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <GraduationCap size={18} />
                    <span>الجامعات والكليات</span>
                  </Link>
                )}

                {/* Vehicles */}
                {hasSectionAccess('VEHICLES') && (
                  <Link
                    to="/admin/vehicles"
                    className={`sidebar-nav-item ${currentPath === '/admin/vehicles' ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Bus size={18} />
                    <span>المركبات والأسطول</span>
                  </Link>
                )}
              </>
            )}

            {/* GROUP 5: النظام والرقابة (System & Security) */}
            <div className="sidebar-group-title mt-4">إعدادات النظام والرقابة</div>

            {/* Staff Management (Super Admin Only) */}
            {!isOperator && (
              <Link
                to="/admin/staff"
                className={`sidebar-nav-item ${
                  currentPath === '/admin/staff' ? 'active' : ''
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Users size={18} />
                <span className="flex-1">إدارة حسابات الموظفين</span>
                <span className="text-[10px] bg-sky-100 text-sky-800 font-bold px-1.5 py-0.5 rounded">صلاحيات</span>
              </Link>
            )}

            {/* Audit Logs */}
            {hasSectionAccess('AUDIT_LOGS') && (
              <Link
                to="/admin/audit-logs"
                className={`sidebar-nav-item ${
                  currentPath === '/admin/audit-logs' ? 'active' : ''
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <ShieldCheck size={18} />
                <span>سجل التدقيق والرقابة</span>
              </Link>
            )}

            {/* Notifications */}
            {hasSectionAccess('NOTIFICATIONS') && (
              <Link
                to="/admin/notifications"
                className={`sidebar-nav-item ${
                  currentPath === '/admin/notifications' ? 'active' : ''
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Bell size={18} />
                <span>الإشعارات الجماعية</span>
              </Link>
            )}

            {/* Settings */}
            {hasSectionAccess('SETTINGS') && (
              <Link
                to="/admin/settings"
                className={`sidebar-nav-item ${currentPath === '/admin/settings' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                <span>الإعدادات العامة</span>
              </Link>
            )}
          </nav>
        </aside>

        {/* Content Area */}
        <main className="admin-content-viewport">
          {currentPath === '/admin/staff' ? (
            !isOperator ? <AdminStaffManagement /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/treasury' ? (
            hasSectionAccess('TREASURY') ? <AdminTreasury /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/student-demand' ? (
            hasSectionAccess('DEMAND') ? <AdminStudentDemand /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/payments' ? (
            hasSectionAccess('PAYMENTS') ? <AdminPaymentVerification onPendingCountChange={setPendingCount} /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/demand-requests' ? (
            hasSectionAccess('DEMAND') ? <AdminDemandRequests /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/audit-logs' ? (
            hasSectionAccess('AUDIT_LOGS') ? <AdminAuditLogs /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/payment-settings' ? (
            hasSectionAccess('PAYMENT_SETTINGS') ? <AdminPaymentSettings /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/bookings' ? (
            hasSectionAccess('BOOKINGS') ? <AdminBookings /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/students' ? (
            hasSectionAccess('STUDENTS') ? <AdminStudents /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/promo-rewards' ? (
            hasSectionAccess('PROMO_REWARDS') ? <AdminPromoAndRewards /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/create-student' ? (
            hasSectionAccess('CREATE_STUDENT') ? <AdminStudentAccountCreator /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/universities' ? (
            hasSectionAccess('UNIVERSITIES') ? <AdminUniversities /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/trips' ? (
            hasSectionAccess('TRIPS') ? <AdminTrips /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/vehicles' ? (
            hasSectionAccess('VEHICLES') ? <AdminVehicles /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/notifications' ? (
            hasSectionAccess('NOTIFICATIONS') ? <AdminNotifications /> : <OperatorAccessDeniedCard />
          ) : currentPath === '/admin/settings' ? (
            hasSectionAccess('SETTINGS') ? <AdminSettings /> : <OperatorAccessDeniedCard />
          ) : (
            hasSectionAccess('DASHBOARD') ? <AdminDashboard pendingCount={pendingCount} /> : <OperatorAccessDeniedCard />
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
